import React from 'react';

import { Add as AddIcon, BorderColor as BorderColorIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { isNotNil } from 'es-toolkit';
import { useNavigate } from 'react-router';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import {
  PeoPayrollProviderCompanyType,
  PeoPayrollProviderInfoFragment,
  PeoPayrollProviderOrderBy,
} from 'src/api';
import {
  Table,
  TableFilter,
  TableRowAction,
  TableSelectedRowId,
  TableTopAction,
} from 'src/components/table';
import { COMPANY_SEARCH_CATEGORIES } from 'src/constants/company';
import { FilterKeys } from 'src/constants/filter';
import { ModalId } from 'src/constants/modals';
import { useFilterStorage, useSearch, useSearchQueryVariables } from 'src/hooks/common';
import { usePeoPayrollProvidersList } from 'src/hooks/peoPayrollProvider';
import { useModal } from 'src/hooks/shared';
import { SortOption } from 'src/types/sort';
import { createOrderByQueryVariable } from 'src/utils/gql';
import {
  buildPeoPayrollProviderDetailsUrl,
  preparePeoPayrollProvidersFilterByStatus,
} from 'src/utils/peoPayrollProvider';

import { PEO_PAYROLL_PROVIDERS_TABLE_COLUMNS } from './constants';
import { prepareSearchFilterByCategories } from './utils/filter';

export const PeoPayrollProvidersTableCVStaff: React.FC = () => {
  const navigate = useNavigate();

  const { openModal } = useModal();

  const { setSearchValue, searchValue, setSearchCategory, searchCategory } = useSearch();
  const {
    getFilterByKey,
    getSortOptionByKey,
    getPaginationQueryVariablesByKey,
    getPaginationObjectByKey,
    setSortOptionByKey,
  } = useFilterStorage();
  const pagination = getPaginationObjectByKey(FilterKeys.PeoPayrollProvidersTableCVStaff);

  const [selectedIds, setSelectedIds] = React.useState<TableSelectedRowId[]>([]);

  const filterQueryVariables = preparePeoPayrollProvidersFilterByStatus(
    getFilterByKey(FilterKeys.PeoPayrollProvidersTableCVStaff),
  );
  const searchQueryVariables = useSearchQueryVariables(prepareSearchFilterByCategories);
  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue, searchCategory]);

  const { peoPayrollProviderList, peoPayrollProvidersCount, loading, refetch } =
    usePeoPayrollProvidersList(
      {
        AND: [
          { companyType: { equals: PeoPayrollProviderCompanyType.PeoPayrollProvider } },
          searchQueryVariables,
          filterQueryVariables,
        ].filter(isNotNil),
      },
      createOrderByQueryVariable(
        getSortOptionByKey(FilterKeys.PeoPayrollProvidersTableCVStaff),
      ) as PeoPayrollProviderOrderBy,
      getPaginationQueryVariablesByKey(FilterKeys.PeoPayrollProvidersTableCVStaff),
      true,
    );

  const refetchData = React.useCallback(async () => {
    await refetch();
    pagination.resetPage();
  }, [pagination, refetch]);

  const handleSort = React.useCallback(
    (sortOption: Nullable<SortOption>) => {
      setSortOptionByKey(FilterKeys.PeoPayrollProvidersTableCVStaff, sortOption);
    },
    [setSortOptionByKey],
  );

  const rowActions = React.useMemo(
    (): Array<TableRowAction<PeoPayrollProviderInfoFragment>> => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: peoPayrollProvider => {
          navigate(buildPeoPayrollProviderDetailsUrl(peoPayrollProvider.id));
        },
      },
    ],
    [navigate],
  );

  const topActions = React.useMemo(
    (): Array<TableTopAction> => [
      {
        actionName: 'Add',
        onAction: () => {
          openModal(ModalId.InvitePeoPayrollProviderByCVStaffModal);
        },
        icon: <AddIcon />,
        show: true,
        render() {
          return (
            <Button
              key={this.actionName}
              variant="contained"
              startIcon={this.icon}
              disabled={loading}
              loading={loading}
              onClick={this.onAction}
            >
              New
            </Button>
          );
        },
      },
    ],
    [loading, openModal],
  );

  return (
    <Table<PeoPayrollProviderInfoFragment>
      dataSource={peoPayrollProviderList}
      rowCount={peoPayrollProvidersCount}
      columns={PEO_PAYROLL_PROVIDERS_TABLE_COLUMNS}
      onSearch={setSearchValue}
      onSearchCategorySelect={setSearchCategory}
      rowActions={rowActions}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      filters={filters}
      topActions={topActions}
      loading={loading}
      onRefresh={refetchData}
      onSort={handleSort}
      pagination={pagination}
      keyFilter={FilterKeys.PeoPayrollProvidersTableCVStaff}
      searchCategories={COMPANY_SEARCH_CATEGORIES}
    />
  );
};

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];
